import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/use-auth";
import type { Stock, StockMouvement } from "@shared/schema";
import {
  Warehouse, Search, Plus, Package, ChevronRight, ChevronLeft, Bot, AlertTriangle,
  TrendingDown, ArrowUpDown, Clock, X, ExternalLink, BarChart3,
  ShoppingCart, TrendingUp, CheckCircle2, ZoomIn,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
  LineChart, Line,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { useProductImageResolver, useRobots } from "@/hooks/use-inventory";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function calcDispo(s: Stock): number {
  return (s.stockPhysique || 0) + (s.stockAVenir || 0) - (s.stockDemo || 0) - (s.stockTampon || 0);
}

function DispoColor(dispo: number, seuil: number) {
  if (dispo <= 0) return "text-red-600 dark:text-red-400";
  if (dispo <= seuil) return "text-orange-500 dark:text-orange-400";
  return "text-green-600 dark:text-green-400";
}

function DispoBadge({ s }: { s: Stock }) {
  const dispo = calcDispo(s);
  const seuil = s.seuilAlerte || 0;
  let variant: "default" | "destructive" | "outline" | "secondary" = "outline";
  let label = `${dispo}`;
  if (dispo <= 0) { variant = "destructive"; label = `${dispo} — Rupture`; }
  else if (dispo <= seuil) { label = `${dispo} — Alerte`; }
  return (
    <Badge variant={variant} className={cn(
      "font-mono text-xs",
      dispo > 0 && dispo <= seuil && "border-orange-400 text-orange-600 dark:text-orange-400",
      dispo > seuil && "border-green-400 text-green-600 dark:text-green-400",
    )}>
      {label}
    </Badge>
  );
}

function TypeBadge({ type }: { type: string | null }) {
  const map: Record<string, string> = {
    Machine: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    pièce_détachée: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
    consommable: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    accessoire: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300",
    iot: "bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300",
  };
  if (!type) return null;
  const label = type === 'pièce_détachée' ? 'Pièce' : type === 'Machine' ? 'Machine' : type.charAt(0).toUpperCase() + type.slice(1);
  return <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium", map[type] || "bg-gray-100 text-gray-800")}>{label}</span>;
}

const TYPE_LABELS: Record<string, string> = {
  entree_commande: "Entrée commande",
  sortie_vente: "Sortie vente",
  entree_retour: "Entrée retour",
  sortie_demo: "Sortie démo",
  entree_achat: "Entrée achat",
  ajustement: "Ajustement",
  entree_tampon: "Entrée tampon",
  sortie_tampon: "Sortie tampon",
};

const MOUVEMENT_TYPES = Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }));

// ─── Mouvement Dialog ─────────────────────────────────────────────────────────

function MouvementDialog({
  stockId, stockNom, open, onOpenChange,
}: { stockId: string; stockNom: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [type, setType] = useState("ajustement");
  const [quantite, setQuantite] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  const mut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`/api/stock/${stockId}/mouvements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typeMouvement: type, quantite: parseInt(quantite), referenceExterne: reference, notes }),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/stock"] });
      qc.invalidateQueries({ queryKey: ["/api/stock", stockId, "mouvements"] });
      toast({ title: "Mouvement enregistré" });
      onOpenChange(false);
      setQuantite(""); setReference(""); setNotes(""); setType("ajustement");
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enregistrer un mouvement</DialogTitle>
          <p className="text-sm text-muted-foreground">{stockNom}</p>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Type de mouvement</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="select-type-mouvement">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MOUVEMENT_TYPES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Quantité</Label>
            <Input data-testid="input-quantite" type="number" value={quantite} onChange={e => setQuantite(e.target.value)} placeholder="Ex: 3" />
          </div>
          <div className="space-y-1">
            <Label>Référence (optionnel)</Label>
            <Input data-testid="input-reference" value={reference} onChange={e => setReference(e.target.value)} placeholder="N° commande, PO..." />
          </div>
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea data-testid="textarea-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button data-testid="button-save-mouvement" onClick={() => mut.mutate()} disabled={!quantite || mut.isPending}>
            {mut.isPending ? "..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Stock Drawer ─────────────────────────────────────────────────────────────

function StockDrawer({ item, open, onClose }: { item: Stock | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const resolveImage = useProductImageResolver();
  const [mouvementOpen, setMouvementOpen] = useState(false);
  const [editField, setEditField] = useState<string | null>(null);
  const [editVal, setEditVal] = useState<string>("");

  const { data: mouvements = [], isLoading: loadMouv } = useQuery<StockMouvement[]>({
    queryKey: ["/api/stock", item?.id, "mouvements"],
    queryFn: async () => {
      const r = await authFetch(`/api/stock/${item!.id}/mouvements`);
      return r.json();
    },
    enabled: !!item?.id && open,
  });

  const updateMut = useMutation({
    mutationFn: async ({ field, value }: { field: string; value: any }) => {
      const r = await authFetch(`/api/stock/${item!.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/stock"] });
      setEditField(null);
      toast({ title: "Mis à jour" });
    },
  });

  if (!item) return null;
  const dispo = calcDispo(item);
  const imgUrl = resolveImage(undefined, undefined);
  const lastMaj = item.derniereMaj ? format(new Date(item.derniereMaj), "dd MMM yyyy HH:mm", { locale: fr }) : "—";

  function NumField({ label, field, value }: { label: string; field: string; value: number | null }) {
    const isEdit = editField === field;
    return (
      <div className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
        <span className="text-sm text-muted-foreground">{label}</span>
        {isEdit ? (
          <div className="flex items-center gap-1">
            <Input className="h-7 w-20 text-right text-sm" type="number" value={editVal}
              onChange={e => setEditVal(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") updateMut.mutate({ field, value: parseInt(editVal) });
                if (e.key === "Escape") setEditField(null);
              }}
              autoFocus />
            <Button size="sm" className="h-7 px-2" onClick={() => updateMut.mutate({ field, value: parseInt(editVal) })}>✓</Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditField(null)}>✕</Button>
          </div>
        ) : (
          <button
            className="font-mono font-semibold text-sm hover:text-primary transition-colors"
            onClick={() => { setEditField(field); setEditVal(String(value ?? 0)); }}
            data-testid={`edit-${field}`}
          >
            {value ?? 0}
          </button>
        )}
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="stock-drawer">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Warehouse className="w-5 h-5 text-primary" />
            <span className="truncate">{item.nom}</span>
            {!item.actif && <Badge variant="secondary" className="text-xs ml-1">Inactif</Badge>}
          </SheetTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <TypeBadge type={item.typeProduit} />
            <span className="font-mono text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{item.sku}</span>
            {item.productId && (
              <Link href={`/products`}>
                <button className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="link-product">
                  <ExternalLink className="w-3 h-3" /> Voir fiche produit
                </button>
              </Link>
            )}
          </div>
        </SheetHeader>

        {/* Stock disponible */}
        <div className={cn("flex items-center justify-between p-4 rounded-lg border mb-4", dispo <= 0 ? "border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800" : dispo <= (item.seuilAlerte || 0) ? "border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800" : "border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800")}>
          <div>
            <p className="text-xs text-muted-foreground">Stock disponible</p>
            <p className={cn("text-3xl font-bold", DispoColor(dispo, item.seuilAlerte || 0))}>{dispo}</p>
            <p className="text-xs text-muted-foreground mt-0.5">= physique + à venir - démo - tampon</p>
          </div>
          <Button data-testid="button-mouvement-drawer" size="sm" onClick={() => setMouvementOpen(true)}>
            <Plus className="w-3 h-3 mr-1" /> Mouvement
          </Button>
        </div>

        {/* Compteurs modifiables */}
        <Card className="mb-4">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold">Compteurs</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <NumField label="Stock physique" field="stockPhysique" value={item.stockPhysique} />
            <NumField label="Stock démo" field="stockDemo" value={item.stockDemo} />
            <NumField label="Stock tampon" field="stockTampon" value={item.stockTampon} />
            <NumField label="Stock à venir" field="stockAVenir" value={item.stockAVenir} />
            <NumField label="Seuil d'alerte" field="seuilAlerte" value={item.seuilAlerte} />
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="mb-4">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold">Notes</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {editField === "notes" ? (
              <div className="space-y-2">
                <Textarea value={editVal} onChange={e => setEditVal(e.target.value)} rows={3} autoFocus />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => updateMut.mutate({ field: "notes", value: editVal })}>Sauvegarder</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditField(null)}>Annuler</Button>
                </div>
              </div>
            ) : (
              <button className="w-full text-left text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[40px]"
                onClick={() => { setEditField("notes"); setEditVal(item.notes || ""); }}
                data-testid="edit-notes">
                {item.notes || <span className="italic">Cliquer pour ajouter des notes…</span>}
              </button>
            )}
          </CardContent>
        </Card>

        {/* Historique mouvements */}
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4" /> Historique des mouvements
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {loadMouv ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : mouvements.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Aucun mouvement enregistré</p>
            ) : (
              <div className="space-y-2">
                {mouvements.map((m: any) => (
                  <div key={m.id} className="flex items-start justify-between py-2 border-b border-border/30 last:border-0" data-testid={`mouvement-${m.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium">{TYPE_LABELS[m.typeMouvement] || m.typeMouvement}</span>
                        {m.referenceExterne && <span className="text-xs text-muted-foreground font-mono bg-muted px-1 rounded">{m.referenceExterne}</span>}
                      </div>
                      {m.notes && <p className="text-xs text-muted-foreground mt-0.5">{m.notes}</p>}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {m.createdAt ? format(new Date(m.createdAt), "dd MMM yyyy HH:mm", { locale: fr }) : "—"}
                      </p>
                    </div>
                    <div className={cn("font-mono font-bold text-sm ml-4", ['sortie_vente', 'sortie_demo', 'sortie_tampon'].includes(m.typeMouvement) ? "text-red-500" : "text-green-600")}>
                      {['sortie_vente', 'sortie_demo', 'sortie_tampon'].includes(m.typeMouvement) ? `-${m.quantite}` : `+${m.quantite}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-right mt-3">Dernière mise à jour : {lastMaj}</p>
      </SheetContent>
      <MouvementDialog stockId={item.id} stockNom={item.nom} open={mouvementOpen} onOpenChange={setMouvementOpen} />
    </Sheet>
  );
}

// ─── Photo Zoom Modal ─────────────────────────────────────────────────────────

type ZoomItem = { images: string[]; nom: string; sku: string };

function PhotoZoomModal({ item, onClose }: { item: ZoomItem | null; onClose: () => void }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [item]);

  const handleKey = useCallback((e: KeyboardEvent) => {
    if (!item) return;
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowLeft") setIdx(i => Math.max(0, i - 1));
    if (e.key === "ArrowRight") setIdx(i => Math.min(item.images.length - 1, i + 1));
  }, [item, onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  if (!item) return null;

  const LABELS = ["Face avant", "Face arrière", "Côté gauche", "Côté droit"];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="photo-zoom-backdrop"
    >
      <div
        className="relative flex flex-col items-center gap-3"
        onClick={e => e.stopPropagation()}
      >
        {/* Fermer */}
        <button
          className="absolute -top-2 -right-2 bg-white/10 hover:bg-white/20 text-white rounded-full p-1.5 transition-colors z-10"
          onClick={onClose}
          data-testid="photo-zoom-close"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* En-tête */}
        <div className="text-white text-center mb-1">
          <p className="font-semibold text-lg leading-tight">{item.nom}</p>
          <p className="text-xs text-gray-400 font-mono">{item.sku}</p>
          {item.images.length > 1 && (
            <p className="text-xs text-gray-300 mt-0.5">{LABELS[idx] || `Photo ${idx + 1}`}</p>
          )}
        </div>

        {/* Image */}
        <img
          src={item.images[idx]}
          alt={`${item.nom} — photo ${idx + 1}`}
          className="object-contain rounded-xl shadow-2xl"
          style={{ maxHeight: "78vh", maxWidth: "80vw" }}
        />

        {/* Navigation */}
        {item.images.length > 1 && (
          <div className="flex items-center gap-6 mt-1">
            <button
              className="flex items-center gap-1.5 text-white text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              onClick={() => setIdx(i => Math.max(0, i - 1))}
              disabled={idx === 0}
              data-testid="photo-zoom-prev"
            >
              <ChevronLeft className="w-4 h-4" /> Précédente
            </button>
            <span className="text-white/60 text-sm font-mono">{idx + 1} / {item.images.length}</span>
            <button
              className="flex items-center gap-1.5 text-white text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              onClick={() => setIdx(i => Math.min(item.images.length - 1, i + 1))}
              disabled={idx === item.images.length - 1}
              data-testid="photo-zoom-next"
            >
              Suivante <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Points de navigation */}
        {item.images.length > 1 && (
          <div className="flex gap-1.5 mt-0.5">
            {item.images.map((_, i) => (
              <button
                key={i}
                className={cn("w-2 h-2 rounded-full transition-colors", i === idx ? "bg-white" : "bg-white/30 hover:bg-white/50")}
                onClick={() => setIdx(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Machine Card ─────────────────────────────────────────────────────────────

function MachineCard({
  item, onClick, images, onPhotoClick,
}: {
  item: Stock;
  onClick: () => void;
  images: string[];
  onPhotoClick: (z: ZoomItem) => void;
}) {
  const dispo = calcDispo(item);
  const imgUrl = images[0] || null;

  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-md group overflow-hidden"
      onClick={onClick}
      data-testid={`stock-card-${item.id}`}
    >
      {/* ── Image 16:9 en haut ─────────────────────────────────────── */}
      <div
        className="relative w-full bg-muted/40 overflow-hidden"
        style={{ aspectRatio: "16/9" }}
        onClick={e => {
          e.stopPropagation();
          if (images.length > 0) onPhotoClick({ images, nom: item.nom, sku: item.sku || "" });
        }}
        data-testid={`machine-photo-${item.id}`}
      >
        {imgUrl ? (
          <>
            <img
              src={imgUrl}
              alt={item.nom}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
            {images.length > 0 && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center cursor-zoom-in">
                <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
              </div>
            )}
            {!item.actif && (
              <Badge variant="secondary" className="absolute top-2 right-2 text-xs shadow">Inactif</Badge>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Bot className="w-10 h-10 text-muted-foreground/40" />
            {!item.actif && (
              <Badge variant="secondary" className="absolute top-2 right-2 text-xs">Inactif</Badge>
            )}
          </div>
        )}
      </div>

      <CardContent className="p-3">
        {/* Nom + SKU */}
        <div className="mb-3">
          <p className="font-semibold text-sm leading-tight group-hover:text-primary transition-colors">{item.nom}</p>
          <p className="text-xs text-muted-foreground font-mono mt-0.5">{item.sku}</p>
        </div>

        {/* Compteurs stock */}
        <div className="grid grid-cols-2 gap-1.5 text-xs">
          <div className="bg-muted/50 rounded-md p-1.5 text-center">
            <p className="text-muted-foreground text-[10px]">Physique</p>
            <p className="font-bold font-mono">{item.stockPhysique ?? 0}</p>
          </div>
          <div className="bg-muted/50 rounded-md p-1.5 text-center">
            <p className="text-muted-foreground text-[10px]">Démo</p>
            <p className="font-bold font-mono">{item.stockDemo ?? 0}</p>
          </div>
          <div className="bg-muted/50 rounded-md p-1.5 text-center">
            <p className="text-muted-foreground text-[10px]">Réservé</p>
            <p className="font-bold font-mono">{item.stockTampon ?? 0}</p>
          </div>
          <div className={cn("rounded-md p-1.5 text-center", dispo <= 0 ? "bg-red-50 dark:bg-red-950/30" : dispo <= (item.seuilAlerte || 0) ? "bg-orange-50 dark:bg-orange-950/30" : "bg-green-50 dark:bg-green-950/30")}>
            <p className={cn("text-[10px]", DispoColor(dispo, item.seuilAlerte || 0))}>Disponible</p>
            <p className={cn("font-bold font-mono", DispoColor(dispo, item.seuilAlerte || 0))}>{dispo}</p>
          </div>
        </div>

        {item.stockAVenir != null && item.stockAVenir > 0 && (
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 font-medium">
            +{item.stockAVenir} en transit
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Accessoire Row ───────────────────────────────────────────────────────────

function AccRow({
  item, onClick, imageUrl, onPhotoClick,
}: {
  item: Stock;
  onClick: () => void;
  imageUrl: string | null;
  onPhotoClick: (z: ZoomItem) => void;
}) {
  const dispo = calcDispo(item);
  const isAlert = dispo <= (item.seuilAlerte || 0);
  return (
    <tr
      className={cn(
        "hover:bg-muted/40 cursor-pointer transition-colors border-b border-border/30",
        dispo <= 0 && "bg-red-50/50 dark:bg-red-950/10",
        dispo > 0 && dispo <= (item.seuilAlerte || 0) && "bg-orange-50/50 dark:bg-orange-950/10",
      )}
      onClick={onClick}
      data-testid={`stock-row-${item.id}`}
    >
      {/* Colonne photo */}
      <td className="py-1.5 px-2 w-14">
        <div
          className="w-12 h-12 rounded-md overflow-hidden bg-muted/60 flex items-center justify-center relative group/photo flex-shrink-0"
          onClick={e => {
            e.stopPropagation();
            if (imageUrl) onPhotoClick({ images: [imageUrl], nom: item.nom, sku: item.sku || "" });
          }}
          style={{ cursor: imageUrl ? "zoom-in" : "default" }}
          data-testid={`acc-photo-${item.id}`}
        >
          {imageUrl ? (
            <>
              <img
                src={imageUrl}
                alt={item.nom}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover/photo:bg-black/20 transition-colors flex items-center justify-center">
                <ZoomIn className="w-3 h-3 text-white opacity-0 group-hover/photo:opacity-100 transition-opacity" />
              </div>
            </>
          ) : (
            <Package className="w-5 h-5 text-muted-foreground/50" />
          )}
        </div>
      </td>
      <td className="py-2.5 px-3 max-w-[220px]">
        <div className="flex items-center gap-2">
          {isAlert && <AlertTriangle className={cn("w-3.5 h-3.5 flex-shrink-0", dispo <= 0 ? "text-red-500" : "text-orange-500")} />}
          <span className="text-sm font-medium truncate">{item.nom}</span>
          {!item.actif && <Badge variant="secondary" className="text-xs ml-1 flex-shrink-0">Inactif</Badge>}
        </div>
      </td>
      <td className="py-2.5 px-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{item.sku}</td>
      <td className="py-2.5 px-3 hidden md:table-cell"><TypeBadge type={item.typeProduit} /></td>
      <td className="py-2.5 px-3 text-center font-mono text-sm font-medium">{item.stockPhysique ?? 0}</td>
      <td className="py-2.5 px-3 text-center font-mono text-sm text-muted-foreground">{item.stockDemo ?? 0}</td>
      <td className="py-2.5 px-3 text-center font-mono text-sm text-muted-foreground hidden lg:table-cell">{item.stockTampon ?? 0}</td>
      <td className="py-2.5 px-3 text-center font-mono text-sm text-muted-foreground hidden lg:table-cell">{item.stockAVenir ?? 0}</td>
      <td className="py-2.5 px-3 text-center">
        <DispoBadge s={item} />
      </td>
      <td className="py-2.5 px-3 text-center font-mono text-xs text-muted-foreground">{item.seuilAlerte ?? 0}</td>
      <td className="py-2.5 px-3 text-right">
        <ChevronRight className="w-4 h-4 text-muted-foreground inline-block" />
      </td>
    </tr>
  );
}

// ─── Analyse Tab ──────────────────────────────────────────────────────────────

type AnalyseData = {
  velocite: Array<{ sku: string; nom: string; mois: string; ventes: number }>;
  couverture: Array<{
    sku: string; nom: string; stockDispo: number;
    velociteMoy: number; couvertureMois: number | null;
    commandesEnTransit: number;
  }>;
  suggestions: Array<{
    sku: string; nom: string; quantiteSuggeree: number;
    urgence: "rouge" | "orange"; justification: string;
  }>;
};

const CHART_COLORS = [
  "#6366f1", "#f59e0b", "#10b981", "#3b82f6", "#f43f5e",
  "#8b5cf6", "#06b6d4", "#84cc16", "#ec4899", "#14b8a6",
];

function CoverageBadge({ mois }: { mois: number | null }) {
  if (mois === null) return <span className="text-muted-foreground text-xs">—</span>;
  if (mois < 1) return <Badge className="bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 border-red-200 text-xs">{mois} mois</Badge>;
  if (mois < 2) return <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 border-orange-200 text-xs">{mois} mois</Badge>;
  return <Badge className="bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400 border-green-200 text-xs">{mois} mois</Badge>;
}

type DateMode = "3m" | "6m" | "12m" | "24m" | "all" | "custom";

const FRENCH_MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const MONTH_NUMS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

function computeRange(mode: DateMode, customFrom: string, customTo: string) {
  if (mode === "custom") {
    return { dateFrom: customFrom || undefined, dateTo: customTo || undefined };
  }
  if (mode === "all") return { dateFrom: undefined, dateTo: undefined };
  const months = ({ "3m": 3, "6m": 6, "12m": 12, "24m": 24 } as Record<string, number>)[mode] || 12;
  const from = new Date();
  from.setMonth(from.getMonth() - months);
  return { dateFrom: from.toISOString().split("T")[0], dateTo: new Date().toISOString().split("T")[0] };
}

function AnalyseTab() {
  const [dateMode, setDateMode] = useState<DateMode>("12m");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedModel, setSelectedModel] = useState<string>("all");

  const { dateFrom, dateTo } = computeRange(dateMode, customFrom, customTo);

  const { data, isLoading } = useQuery<AnalyseData>({
    queryKey: ["/api/stock/analyse", dateFrom ?? "all", dateTo ?? "all"],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      const r = await authFetch(`/api/stock/analyse?${params}`);
      return r.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const allModels = Array.from(new Set((data?.velocite || []).map(v => v.nom)));
  const filteredVelocite = !data ? [] : selectedModel === "all"
    ? data.velocite
    : data.velocite.filter(v => v.nom === selectedModel);

  // ── LineChart multi-années : X = mois, une courbe par année ──────────────
  const years = Array.from(new Set(filteredVelocite.map(v => v.mois.slice(0, 4)))).sort();
  const lineChartData = MONTH_NUMS.map((mm, idx) => {
    const entry: Record<string, any> = { mois: FRENCH_MONTHS[idx] };
    years.forEach(year => {
      const monthKey = `${year}-${mm}`;
      const total = filteredVelocite
        .filter(v => v.mois.slice(0, 7) === monthKey)
        .reduce((sum, v) => sum + v.ventes, 0);
      entry[year] = total > 0 ? total : null;
    });
    return entry;
  });
  const hasChartData = lineChartData.some(d => years.some(y => d[y] !== null));

  const emptyHistorique = !data || (data.velocite.length === 0 && data.couverture.every(c => c.velociteMoy === 0));
  const hasSuggestions = data && data.suggestions.length > 0;

  const DATE_LABELS: Record<DateMode, string> = {
    "3m": "3 mois", "6m": "6 mois", "12m": "12 mois", "24m": "24 mois", all: "Tout", custom: "Personnalisé",
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}
      </div>
    );
  }

  if (!data) return <p className="text-sm text-red-500">Erreur de chargement</p>;

  return (
    <div className="space-y-6">

      {/* ── Sélecteur de plage de dates ──────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        {(["3m", "6m", "12m", "24m", "all", "custom"] as DateMode[]).map(m => (
          <Button
            key={m}
            size="sm"
            variant={dateMode === m ? "default" : "outline"}
            className="h-8 text-xs px-3"
            onClick={() => setDateMode(m)}
            data-testid={`date-mode-${m}`}
          >
            {DATE_LABELS[m]}
          </Button>
        ))}
        {dateMode === "custom" && (
          <>
            <Input
              type="date"
              value={customFrom}
              onChange={e => setCustomFrom(e.target.value)}
              className="h-8 text-xs w-36"
              data-testid="input-date-from"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <Input
              type="date"
              value={customTo}
              onChange={e => setCustomTo(e.target.value)}
              className="h-8 text-xs w-36"
              data-testid="input-date-to"
            />
          </>
        )}
      </div>

      {emptyHistorique && (
        <div className="rounded-xl border border-dashed border-border p-6 text-center text-muted-foreground">
          <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucune donnée disponible pour cette période.</p>
          <p className="text-xs mt-1">Un snapshot automatique est effectué le 1er de chaque mois.</p>
        </div>
      )}

      {/* ── BLOC 1 — Vélocité (LineChart multi-années) ───────────────── */}
      {data.velocite.length > 0 && (
        <Card className="shadow-sm border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Vélocité par modèle — comparaison annuelle
              </span>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger className="w-44 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les modèles</SelectItem>
                  {allModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!hasChartData ? (
              <p className="text-sm text-muted-foreground text-center py-8">Aucune donnée disponible pour cette période.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={lineChartData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))" }}
                    formatter={(value: any, name: any) => [value ?? "—", name]}
                  />
                  {years.length > 0 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                  {years.map((year, i) => (
                    <Line
                      key={year}
                      dataKey={year}
                      stroke={CHART_COLORS[i % CHART_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3, fill: CHART_COLORS[i % CHART_COLORS.length] }}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── BLOC 2 — Couverture de stock ──────────────────────────── */}
      <Card className="shadow-sm border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="w-4 h-4 text-blue-500" />
            Couverture de stock
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 bg-muted/30">
                <tr>
                  <th className="py-2.5 px-4 text-xs font-medium text-muted-foreground text-left">Modèle</th>
                  <th className="py-2.5 px-4 text-xs font-medium text-muted-foreground text-center">Stock dispo</th>
                  <th className="py-2.5 px-4 text-xs font-medium text-muted-foreground text-center">Vélocité moy.</th>
                  <th className="py-2.5 px-4 text-xs font-medium text-muted-foreground text-center">Couverture</th>
                  <th className="py-2.5 px-4 text-xs font-medium text-muted-foreground text-center hidden md:table-cell">En transit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {data.couverture.map((c, i) => (
                  <tr key={`${c.sku}-${i}`} className="hover:bg-muted/20">
                    <td className="py-2.5 px-4">
                      <div>
                        <p className="font-medium text-sm">{c.nom}</p>
                        <p className="text-xs text-muted-foreground font-mono">{c.sku}</p>
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-center font-mono font-bold">
                      <span className={cn(c.stockDispo <= 0 ? "text-red-600 dark:text-red-400" : c.stockDispo <= 2 ? "text-orange-500" : "text-green-600 dark:text-green-400")}>
                        {c.stockDispo}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-center text-xs text-muted-foreground">
                      {c.velociteMoy > 0 ? `${c.velociteMoy}/mois` : "—"}
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      {c.velociteMoy === 0 ? <span className="text-muted-foreground text-xs">—</span> : <CoverageBadge mois={c.couvertureMois} />}
                    </td>
                    <td className="py-2.5 px-4 text-center text-xs font-mono hidden md:table-cell text-muted-foreground">
                      {c.commandesEnTransit > 0 ? (
                        <span className="text-blue-600 dark:text-blue-400 font-medium">+{c.commandesEnTransit}</span>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
                {data.couverture.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground text-sm">Aucune donnée disponible</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ── BLOC 3 — Suggestions de réapprovisionnement ───────────── */}
      <Card className="shadow-sm border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingCart className="w-4 h-4 text-orange-500" />
            Suggestions de réapprovisionnement
            <span className="text-xs font-normal text-muted-foreground ml-1">(calculé automatiquement)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!hasSuggestions ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <p className="text-sm text-green-700 dark:text-green-400 font-medium">Tous les modèles ont une couverture suffisante.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.suggestions.map((s, i) => (
                <div
                  key={`${s.sku}-${i}`}
                  data-testid={`suggestion-${s.sku}`}
                  className={cn(
                    "flex items-start gap-4 p-3.5 rounded-xl border",
                    s.urgence === "rouge"
                      ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
                      : "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800"
                  )}
                >
                  <AlertTriangle className={cn("w-4 h-4 mt-0.5 flex-shrink-0", s.urgence === "rouge" ? "text-red-600 dark:text-red-400" : "text-orange-500")} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-medium text-sm">{s.nom}</p>
                      <Badge className={cn("text-xs", s.urgence === "rouge" ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 border-red-200" : "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400 border-orange-200")}>
                        {s.urgence === "rouge" ? "Urgent" : "À prévoir"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{s.justification}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={cn("text-xl font-bold", s.urgence === "rouge" ? "text-red-600 dark:text-red-400" : "text-orange-500")}>{s.quantiteSuggeree}</p>
                    <p className="text-xs text-muted-foreground">unités</p>
                  </div>
                </div>
              ))}
              <div className="pt-2">
                <Link href="/achats">
                  <Button variant="outline" size="sm" className="w-full gap-2" data-testid="button-creer-bon-commande">
                    <ShoppingCart className="w-4 h-4" />
                    Créer un bon de commande
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function StockPage() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statutFilter, setStatutFilter] = useState("all");
  const [showInactif, setShowInactif] = useState(false);
  const [selected, setSelected] = useState<Stock | null>(null);
  const [mouvementOpen, setMouvementOpen] = useState(false);
  const [sortField, setSortField] = useState<string>("nom");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [zoom, setZoom] = useState<ZoomItem | null>(null);

  // ── Croisement SKU → images (une seule requête groupée) ──────────────────
  const resolveImage = useProductImageResolver();
  const { data: robots = [] } = useRobots();

  // Map SKU → photos[] (4 faces) pour les machines
  const robotPhotoMap = Object.fromEntries(
    (robots as any[])
      .filter((r: any) => r.sku)
      .map((r: any) => [
        r.sku,
        [r.photoFrontUrl, r.photoBackUrl, r.photoLeftUrl, r.photoRightUrl].filter(Boolean) as string[],
      ])
  ) as Record<string, string[]>;

  const { data: items = [], isLoading, error } = useQuery<Stock[]>({
    queryKey: ["/api/stock", typeFilter, search, showInactif],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.set("type", typeFilter);
      if (search) params.set("search", search);
      if (!showInactif) params.set("actif", "true");
      const r = await authFetch(`/api/stock?${params}`);
      return r.json();
    },
  });

  const machines = items.filter(i => i.typeProduit === "Machine");
  const accessories = items.filter(i => i.typeProduit !== "Machine");

  function applyFilters(list: Stock[]) {
    let r = [...list];
    if (statutFilter === "rupture") r = r.filter(i => calcDispo(i) <= 0);
    else if (statutFilter === "alerte") r = r.filter(i => { const d = calcDispo(i); return d > 0 && d <= (i.seuilAlerte || 0); });
    else if (statutFilter === "disponible") r = r.filter(i => calcDispo(i) > (i.seuilAlerte || 0));
    return r;
  }

  function sortList(list: Stock[]) {
    return [...list].sort((a: any, b: any) => {
      let va = a[sortField]; let vb = b[sortField];
      if (sortField === "dispo") { va = calcDispo(a); vb = calcDispo(b); }
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }

  function toggleSort(field: string) {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  }

  const filteredMachines = applyFilters(machines);
  const filteredAccessories = sortList(applyFilters(accessories));

  const lastMaj = items.length > 0 ? items.reduce((best, i) => {
    if (!i.derniereMaj) return best;
    return !best || new Date(i.derniereMaj) > new Date(best) ? i.derniereMaj : best;
  }, null as any) : null;

  const [tab, setTab] = useState("machines");

  function handleTabChange(v: string) {
    setTab(v);
    if (v === "machines") setTypeFilter("Machine");
    else if (v === "accessories") setTypeFilter("all");
    // "analyse" tab doesn't change type filter
  }

  const SortTh = ({ field, label }: { field: string; label: string }) => (
    <th
      className="py-2.5 px-3 text-xs font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={cn("w-3 h-3", sortField === field ? "text-primary" : "text-muted-foreground/40")} />
      </span>
    </th>
  );

  return (
    <div className="p-6 space-y-6 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Warehouse className="w-6 h-6 text-primary" />
            Stock
            <Badge variant="outline" className="font-mono ml-1" data-testid="badge-total">{items.length}</Badge>
          </h1>
          {lastMaj && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Dernière mise à jour : {format(new Date(lastMaj), "dd MMM yyyy HH:mm", { locale: fr })}
            </p>
          )}
        </div>
        <Button data-testid="button-new-mouvement" onClick={() => { setSelected(null); setMouvementOpen(false); }}>
          <Plus className="w-4 h-4 mr-1" /> Enregistrer un mouvement
        </Button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="input-search"
            className="pl-8"
            placeholder="Rechercher nom ou SKU…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Select value={typeFilter} onValueChange={v => setTypeFilter(v)}>
          <SelectTrigger className="w-40" data-testid="select-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="Machine">Machine</SelectItem>
            <SelectItem value="pièce_détachée">Pièce détachée</SelectItem>
            <SelectItem value="consommable">Consommable</SelectItem>
            <SelectItem value="accessoire">Accessoire</SelectItem>
            <SelectItem value="iot">IoT</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statutFilter} onValueChange={setStatutFilter}>
          <SelectTrigger className="w-40" data-testid="select-statut">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="disponible">Disponible</SelectItem>
            <SelectItem value="alerte">En alerte</SelectItem>
            <SelectItem value="rupture">Rupture</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={showInactif ? "default" : "outline"}
          size="sm"
          onClick={() => setShowInactif(v => !v)}
          data-testid="toggle-inactif"
        >
          {showInactif ? <><X className="w-3 h-3 mr-1" /> Masquer inactifs</> : "Inclure inactifs"}
        </Button>
      </div>

      {/* Onglets */}
      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="machines" data-testid="tab-machines">
            Machines
            <Badge variant="outline" className="ml-2 font-mono text-xs">{filteredMachines.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="accessories" data-testid="tab-accessories">
            Accessoires &amp; Pièces
            <Badge variant="outline" className="ml-2 font-mono text-xs">{filteredAccessories.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="analyse" data-testid="tab-analyse">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
            Analyse
          </TabsTrigger>
        </TabsList>

        {/* ── Onglet Machines ─────────────────────────────────────────────── */}
        <TabsContent value="machines" className="mt-4">
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-52 w-full rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-red-500">Erreur de chargement</p>
          ) : filteredMachines.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-muted-foreground">
              <Bot className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">Aucune machine trouvée</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredMachines.map(m => {
                const robotPhotos = m.sku ? (robotPhotoMap[m.sku] || []) : [];
                const productImg = resolveImage(m.sku, undefined);
                const images = robotPhotos.length > 0 ? robotPhotos : (productImg ? [productImg] : []);
                return (
                  <MachineCard
                    key={m.id}
                    item={m}
                    onClick={() => setSelected(m)}
                    images={images}
                    onPhotoClick={setZoom}
                  />
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Onglet Accessoires ──────────────────────────────────────────── */}
        <TabsContent value="accessories" className="mt-4">
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : error ? (
            <p className="text-sm text-red-500">Erreur de chargement</p>
          ) : filteredAccessories.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-muted-foreground">
              <Package className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">Aucun accessoire ou pièce trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
              <table className="w-full text-sm">
                <thead className="border-b border-border/60 bg-muted/30">
                  <tr>
                    <th className="py-2.5 px-2 w-14 text-xs font-medium text-muted-foreground text-left">Photo</th>
                    <SortTh field="nom" label="Nom" />
                    <th className="py-2.5 px-3 text-xs font-medium text-muted-foreground text-left">SKU</th>
                    <th className="py-2.5 px-3 text-xs font-medium text-muted-foreground hidden md:table-cell text-left">Type</th>
                    <SortTh field="stockPhysique" label="Physique" />
                    <SortTh field="stockDemo" label="Démo" />
                    <th className="py-2.5 px-3 text-xs font-medium text-muted-foreground hidden lg:table-cell text-center">Tampon</th>
                    <th className="py-2.5 px-3 text-xs font-medium text-muted-foreground hidden lg:table-cell text-center">À venir</th>
                    <SortTh field="dispo" label="Disponible" />
                    <th className="py-2.5 px-3 text-xs font-medium text-muted-foreground text-center">Seuil</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredAccessories.map(a => (
                    <AccRow
                      key={a.id}
                      item={a}
                      onClick={() => setSelected(a)}
                      imageUrl={resolveImage(a.sku, undefined)}
                      onPhotoClick={setZoom}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        {/* ── Onglet Analyse ──────────────────────────────────────────────── */}
        <TabsContent value="analyse" className="mt-4">
          <AnalyseTab />
        </TabsContent>
      </Tabs>

      {/* Drawer détail */}
      <StockDrawer item={selected} open={!!selected} onClose={() => setSelected(null)} />

      {/* Zoom photo */}
      <PhotoZoomModal item={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}
